import React from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import consultantsImage from '../assets/images/Image 4_Consultants.png';
import SEO from '../components/SEO';

const styles = `
  .feature-card {
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(255, 255, 255, 0.2);
    transition: all 0.3s ease;
  }
  .feature-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
  .feature-icon {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
  .learn-more-text {
    color: #2563eb;
    font-weight: 500;
    margin-top: 1rem;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  .feature-card:hover .learn-more-text {
    opacity: 1;
  }
  .pulse-glow {
    animation: pulse 2s infinite;
  }
  @keyframes pulse {
    0% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.4); }
    70% { box-shadow: 0 0 0 10px rgba(37, 99, 235, 0); }
    100% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0); }
  }
`;

const tips = [
  {
    icon: "⏱️",
    title: "Time Management",
    text: "BCG cases are timed. Practice structuring your approach and allocating time across understanding the prompt, analyzing exhibits, and forming recommendations."
  },
  {
    icon: "📊",
    title: "Exhibit Interpretation",
    text: "Focus on reading charts, tables, and data exhibits quickly and accurately—they are central to BCG candidate-led and Casey-style cases."
  },
  {
    icon: "🎯",
    title: "Hypothesis-Driven Thinking",
    text: "Develop a clear hypothesis and test it with data. BCG looks for structured, logical reasoning rather than jumping to conclusions."
  },
  {
    icon: "📝",
    title: "Practice Cases",
    text: "Regular practice with BCG-style cases builds familiarity with formats, question types, and the level of numerical precision expected."
  },
  {
    icon: "🧮",
    title: "Mental Math",
    text: "Sharpen your mental math—you'll need to do quick calculations and sanity checks without a calculator in timed conditions."
  },
  {
    icon: "🎮",
    title: "Case Mechanics",
    text: "Familiarize yourself with the Casey assessment flow: case brief, exhibits, and question types before the real assessment."
  }
];

const pitfalls = [
  {
    icon: "❌",
    title: "Rushing Through",
    text: "Don't rush through the case brief or exhibits without fully understanding the situation and what is being asked."
  },
  {
    icon: "⚠️",
    title: "Ignoring Instructions",
    text: "Always read instructions and question stems carefully—they define scope, format, and what a good answer looks like."
  },
  {
    icon: "🔄",
    title: "No Structure",
    text: "Going in without a clear structure leads to poor time use and weaker recommendations. Outline before you answer."
  },
  {
    icon: "📉",
    title: "Poor Data Analysis",
    text: "Failing to use exhibits correctly or misreading numbers leads to wrong conclusions. Double-check key figures."
  }
];

const whyCognidoorBcg = [
  {
    icon: "🧮",
    title: "BCG Casey Simulation",
    text: "Practice the full Casey assessment flow with timed questions, exhibits, and feedback aligned to BCG-style cases.",
    link: "/bcg-casey-simulation"
  },
  {
    icon: "📚",
    title: "Quick Consulting Math & Chart Guides",
    text: "Access guides and practice for the math and chart skills you need in BCG cases and candidate-led interviews.",
    link: "/consulting-math-guides"
  },
  {
    icon: "🎯",
    title: "BCG Casey Guide",
    text: "Learn how BCG Casey works, question types, and strategies from our comprehensive Casey guide.",
    link: "/bcg-casey-guide"
  },
  {
    icon: "⚡",
    title: "BCG Candidate-Led AI Coach",
    text: "Practice with an AI coach that simulates a candidate-led case interview and gives structured feedback.",
    link: "/bcg-ai-coach"
  },
  {
    icon: "💡",
    title: "Expert Tips",
    text: "Learn proven strategies and time management techniques for BCG-style case and Casey assessments.",
    link: "/about-bcg-casey"
  },
  {
    icon: "🔓",
    title: "Flexible Access",
    text: "Choose between Basic and Pro tiers to access the features that match your BCG preparation needs.",
    link: "/"
  }
];

const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

const ctaPrimary =
  'inline-flex items-center justify-center px-4 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-emerald-600 to-teal-500 text-white shadow-sm hover:shadow-md transition-all';
const ctaSecondary =
  'inline-flex items-center justify-center px-4 py-2.5 rounded-xl text-sm font-semibold border-2 border-emerald-600 text-emerald-800 bg-white hover:bg-emerald-50 transition-colors';
const ctaMuted =
  'inline-flex items-center justify-center px-4 py-2.5 rounded-xl text-sm font-medium border border-slate-200 text-slate-700 bg-white hover:border-slate-300 hover:bg-slate-50 transition-colors';
const ctaExternal =
  'inline-flex items-center justify-center px-4 py-2.5 rounded-xl text-sm font-medium border border-slate-300 text-slate-600 bg-slate-50 hover:bg-slate-100 transition-colors';

function BcgCaseyCtaStrip() {
  return (
    <section
      className="relative z-20 bg-gradient-to-b from-emerald-50/40 to-white border-y border-emerald-100/80 py-6 sm:py-8 px-4"
      aria-label="Quick links for BCG Casey prep"
    >
      <p className="text-center text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">
        Practice on Cognidoor
      </p>
      <div className="flex flex-wrap justify-center gap-2 sm:gap-3 max-w-4xl mx-auto">
        <Link to="/bcg-ai-coach" className={ctaPrimary}>
          BCG AI Coach
        </Link>
        <Link to="/bcg-casey-simulation" className={ctaSecondary}>
          BCG Casey simulation
        </Link>
        <Link to="/bcg-casey-guide" className={ctaSecondary}>
          BCG Casey guide
        </Link>
        <Link to="/consulting-math-guides" className={ctaSecondary}>
          Math &amp; chart guides
        </Link>
        <Link to="/mckinsey-ai-coach" className={ctaMuted}>
          McKinsey AI Coach
        </Link>
        <Link to="/about" className={ctaMuted}>
          About Cognidoor
        </Link>
        <Link to="/about-solve" className={ctaMuted}>
          About McKinsey Solve
        </Link>
      </div>
      <p className="text-center text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500 mt-8 mb-3">
        Official BCG
      </p>
      <div className="flex flex-wrap justify-center gap-2 sm:gap-3 max-w-3xl mx-auto">
        <a
          href="https://www.bcg.com/careers/working-at-bcg/interviewing"
          target="_blank"
          rel="noopener noreferrer"
          className={ctaExternal}
        >
          BCG interviewing
        </a>
        <a
          href="https://www.bcg.com/careers"
          target="_blank"
          rel="noopener noreferrer"
          className={ctaExternal}
        >
          BCG careers
        </a>
      </div>
    </section>
  );
}

export default function AboutBcgCaseyPage() {
  const navigate = useNavigate();

  const handleCardClick = (link) => {
    if (link) {
      navigate(link);
    }
  };

  return (
    <>
      <SEO
        title="About BCG Casey Assessment"
        description="Learn about the BCG Casey online case assessment: format, timing, exhibit types, and how to prepare effectively. Understand what to expect and how Cognidoor helps you practice."
        url="/about-bcg-casey"
        noIndex
      />
      <div className="bg-white text-gray-900 font-sans">
        {/* Hero Section */}
        <section className="relative z-30 bg-gradient-to-br from-blue-100 via-blue-50 to-white pb-6 sm:pb-12 min-h-[320px] sm:min-h-[500px] flex items-center overflow-visible">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900/90 via-gray-800/85 to-gray-700/80">
            <div className="absolute inset-0" style={{
              backgroundImage: "url('https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80')",
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              mixBlendMode: 'overlay'
            }} />
          </div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="absolute z-10 hidden md:block hero-image w-[200px] md:w-[300px] lg:w-[400px] aspect-[2/3]"
            style={{ right: '2vw', top: '24px' }}
          >
            <div className="h-full rounded-xl overflow-hidden shadow-2xl transform hover:scale-[1.02] transition-transform duration-500">
              <img
                src={consultantsImage}
                alt="Consultants"
                className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-700"
              />
            </div>
          </motion.div>
          <div className="container pl-3 sm:pl-[36px] md:pl-[72px] grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 py-10 sm:py-24 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-2xl hero-content"
            >
              <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 leading-tight text-white">
                About BCG Casey
              </h1>
              <p className="text-base sm:text-xl md:text-2xl text-white/90 mb-6 sm:mb-10 leading-relaxed">
                BCG Casey is a digital case assessment that tests your structuring, analytical, and numerical skills through candidate-led style cases and exhibits. Learn how to master it below!
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-3">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/bcg-casey-simulation')}
                  className="group relative px-5 py-3 sm:px-8 sm:py-4 bg-gradient-to-r from-emerald-600 to-teal-500 text-white text-base sm:text-lg rounded-xl shadow-md hover:shadow-xl transition-all duration-300"
                >
                  Try BCG Casey simulation
                  <div className="absolute inset-0 bg-white rounded-xl opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                </motion.button>
                <Link
                  to="/bcg-ai-coach"
                  className="inline-flex items-center justify-center px-5 py-3 sm:px-8 sm:py-4 border-2 border-white/70 text-white text-base sm:text-lg font-semibold rounded-xl hover:bg-white/10 transition-all"
                >
                  BCG AI Coach
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        <BcgCaseyCtaStrip />

        {/* Assessment Structure */}
        <section className="py-10 sm:py-20 bg-gradient-to-b from-white via-blue-50/30 to-white text-center px-3 sm:px-6 md:px-10 relative">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 sm:mb-12 section-title tracking-tight">What BCG Casey Covers</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-8 max-w-7xl mx-auto">
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/bcg-casey-guide')}
              className="feature-card rounded-2xl shadow-md p-4 sm:p-6 md:p-8 backdrop-blur-sm cursor-pointer"
            >
              <div className="feature-icon">📋</div>
              <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3 text-gray-800">Case Brief & Structuring</h3>
              <p className="text-sm text-gray-600 leading-relaxed">Understand the situation, clarify the objective, and structure your approach before diving into data and exhibits.</p>
              <div className="learn-more-text">Learn More</div>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/consulting-math-guides')}
              className="feature-card rounded-2xl shadow-md p-4 sm:p-6 md:p-8 backdrop-blur-sm cursor-pointer"
            >
              <div className="feature-icon">📊</div>
              <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3 text-gray-800">Exhibits & Math</h3>
              <p className="text-sm text-gray-600 leading-relaxed">Interpret charts and tables, perform quick calculations, and use data to support your hypotheses and recommendations.</p>
              <div className="learn-more-text">Learn More</div>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/bcg-ai-coach')}
              className="feature-card rounded-2xl shadow-md p-4 sm:p-6 md:p-8 backdrop-blur-sm cursor-pointer"
            >
              <div className="feature-icon">🎯</div>
              <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3 text-gray-800">Candidate-Led Interview</h3>
              <p className="text-sm text-gray-600 leading-relaxed">Practice driving the case with an AI coach: ask for exhibits, run calculations, and deliver a clear recommendation.</p>
              <div className="learn-more-text">Learn More</div>
            </motion.div>
          </div>
        </section>

        {/* Tips Section */}
        <section className="py-10 sm:py-20 bg-gradient-to-b from-white via-blue-50/20 to-white px-3 sm:px-6 md:px-10 text-center relative">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 section-title tracking-tight">Best Tips for Acing BCG Casey</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8 max-w-7xl mx-auto">
            {tips.map((tip, idx) => (
              <div key={idx} className="feature-card rounded-2xl shadow-md p-4 sm:p-6 md:p-8 backdrop-blur-sm" style={{ animationDelay: `${idx * 0.1}s` }}>
                <div className="feature-icon">{tip.icon}</div>
                <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3 text-gray-800">{tip.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{tip.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Pitfalls Section */}
        <section className="py-10 sm:py-20 bg-gradient-to-b from-white via-blue-50/30 to-white text-center px-3 sm:px-6 md:px-10 relative">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 section-title tracking-tight">Common Pitfalls & How to Avoid Them</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8 max-w-7xl mx-auto">
            {pitfalls.map((pit, idx) => (
              <div key={idx} className="feature-card rounded-2xl shadow-md p-4 sm:p-6 md:p-8 backdrop-blur-sm" style={{ animationDelay: `${idx * 0.1}s` }}>
                <div className="feature-icon">{pit.icon}</div>
                <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3 text-gray-800">{pit.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{pit.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Why Cognidoor Section */}
        <section className="py-10 sm:py-20 bg-gradient-to-b from-white via-blue-50/20 to-white px-3 sm:px-6 md:px-10 text-center relative">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 section-title tracking-tight">Why Cognidoor is the Best Tool for BCG Casey</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8 max-w-7xl mx-auto">
            {whyCognidoorBcg.map((item, idx) => (
              <motion.div
                key={idx}
                className={`feature-card rounded-2xl shadow-md p-4 sm:p-6 md:p-8 backdrop-blur-sm cursor-pointer ${item.link ? 'pulse-glow' : ''}`}
                style={{ animationDelay: `${idx * 0.1}s` }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleCardClick(item.link)}
              >
                <div className="feature-icon">{item.icon}</div>
                <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3 text-gray-800">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <BcgCaseyCtaStrip />
      </div>
    </>
  );
}
